/**
 * Profile edit screen for one family member (parent or student).
 */
import { Stack, useRouter } from "expo-router";
import { HeaderBackButton } from "expo-router/build/react-navigation";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Platform } from "react-native";

import { toastSuccess } from "@/components/AppToast";
import {
    ProfileForm,
    ProfileFormActions,
    ProfileScreenLayout,
    useConfirmDiscardUnsavedChanges,
    useProfileForm,
} from "@/features/profile";
import { goBackOrReplace } from "@/lib/navigation/goBackOrReplace";
import { TEST_IDS } from "../../../testIdDefinition";

export default function ProfileEditScreen() {
    const { person, readOnly, isSaving, saveError, save, isDirty } =
        useProfileForm();
    const editable = person.can_edit;
    const router = useRouter();
    const { t } = useTranslation();

    const allowLeave = useConfirmDiscardUnsavedChanges(isDirty);

    const handleSave = useCallback(async () => {
        const didSave = await save();
        if (!didSave) {
            return;
        }

        toastSuccess(t("profile.savedTitle"), t("profile.savedBody"));
        allowLeave();
        goBackOrReplace("/family");
    }, [allowLeave, save, t]);

    const title =
        `${person.first_name ?? ""} ${person.last_name ?? ""}`.trim() ||
        t(`profile.title.${person.role.toLowerCase()}Profile`);

    const description = editable
        ? t(`profile.title.description.${person.role.toLowerCase()}`)
        : t(`profile.title.descriptionReadOnly.${person.role.toLowerCase()}`);

    return (
        <>
            <Stack.Screen
                options={{
                    title,
                    // We must explicitly add Back button - on iOS it is not shown on first screen in stack
                    // (in this case the nested stack for "profile/[pipedriveId]")
                    headerLeft: ({ tintColor }) => (
                        <HeaderBackButton
                            tintColor={tintColor}
                            onPress={() => router.back()}
                            testID={TEST_IDS[TEST_IDS.PROFILE_BACK_BUTTON]}
                        />
                    ),
                }}
            />
            <ProfileScreenLayout
                description={description}
                footer={
                    isDirty || Platform.OS === "web" ? (
                        <ProfileFormActions
                            readOnly={readOnly}
                            isSaving={isSaving}
                            saveError={saveError}
                            onSave={handleSave}
                        />
                    ) : null
                }
            >
                <ProfileForm />
            </ProfileScreenLayout>
        </>
    );
}
